import React, { useContext, useRef, useState } from 'react';
import { NavLink } from 'react-router-dom';
import gsap from 'gsap';
import { useGSAP } from '@gsap/react';
import BgColorContainer from '../../Layout/BgColorContainer';
import LazyVideo from '../../ui/cinematic/LazyVideo';
import CursorContext from '../../providers/Cursor';
import useReveal from '../../../hooks/useReveal';
import useCursorZone from '../../../hooks/useCursorZone';
import { fotoServicio, useServicios } from '../../../cms/servicios';
import { ConAcento, elemento } from '../../../cms/textos';
import previewTransformacion from '../../../assets/home/servicios/transformacion-digital.webp';
import previewMarketing from '../../../assets/home/servicios/marketing-digital.webp';
import previewMarca from '../../../assets/home/servicios/marca-y-diseno.webp';
import previewDesarrollo from '../../../assets/home/servicios/desarrollo-web-y-aplicaciones.webp';
import previewAwnaGo from '../../../assets/home/servicios/app-awnago.webp';
import previewCx from '../../../assets/home/servicios/customer-experience.webp';
// Recodificado a 720×960 (1 MB): el original de S3 pesaba 11 MB a 8,8 Mbps para una columna de fondo
import particulasVideo from '../../../assets/home/servicios-particulas.mp4';

/**
 * Miniaturas de la vista previa (640 px, WebP). Los PNG de `imagen` pesan
 * ~1 MB cada uno para mostrarse a 288 px; quedan solo como respaldo si se
 * agrega un servicio nuevo sin miniatura.
 */
const PREVIEWS = {
    'transformacion-digital': previewTransformacion,
    'marketing-digital': previewMarketing,
    'marca-y-diseno': previewMarca,
    'desarrollo-web-y-aplicaciones': previewDesarrollo,
    'app-awnago': previewAwnaGo,
    'customer-experience': previewCx,
};

/** Bajada corta: el subtítulo si existe, si no lo que viene después de los ":" del título. */
const bajada = (s) => s.subtitulo || (s.titulo || '').split(':').slice(1).join(':').trim();

/** Textos de "Páginas → Inicio" (Home.jsx); la lista, de "Servicios". */
function Servicios({ textos }) {
    const sectionRef = useRef(null);
    const listRef = useReveal({ stagger: 60 });
    const previewRef = useRef(null);
    const moveTo = useRef(null);
    const [activo, setActivo] = useState(null);
    const { navBarEnter } = useContext(CursorContext);
    const cursorZone = useCursorZone('dark');
    const Ceja = elemento(textos, 'ceja', 'p');
    const Titulo = elemento(textos, 'titular', 'h2');
    const Bajada = elemento(textos, 'bajada', 'p');
    const { servicios } = useServicios();

    // La vista previa sigue al cursor con inercia (quickTo evita crear un tween por evento)
    useGSAP(() => {
        moveTo.current = {
            x: gsap.quickTo(previewRef.current, 'x', { duration: 0.6, ease: 'power3' }),
            y: gsap.quickTo(previewRef.current, 'y', { duration: 0.6, ease: 'power3' }),
        };
    }, { scope: sectionRef });

    const onMove = (e) => {
        const rect = sectionRef.current.getBoundingClientRect();
        moveTo.current?.x(e.clientX - rect.left);
        moveTo.current?.y(e.clientY - rect.top);
    };

    return (
        <BgColorContainer>
            {/* overflow-clip (no hidden): hidden crea un contenedor de scroll y rompe el sticky del video */}
            <section ref={sectionRef} id="servicios" className="relative overflow-clip bg-ink py-28 text-cream md:py-40" {...cursorZone}>
                {/* Partículas a la derecha, sin overlay. El clip es vertical (900×1200):
                    va sticky a la altura de la pantalla y en contain para no ampliarse
                    (con cover en pantallas anchas se pixelaba). Los bordes se funden
                    con una máscara porque su negro no es exactamente el --ink. */}
                <div aria-hidden="true" className="pointer-events-none absolute inset-y-0 right-0 hidden w-[42%] lg:block">
                    <div className="video-feather sticky top-0 h-svh">
                        <LazyVideo src={particulasVideo} className="h-full w-full object-contain object-right" />
                    </div>
                </div>

                <div className="container relative px-5 md:px-10 2xl:px-0">
                    <div className="flex flex-col gap-6 md:max-w-3xl">
                        <Ceja className="flex items-center gap-4 text-xs uppercase tracking-[0.3em] text-cream/50 md:text-sm">
                            <span>03</span>
                            <span className="h-px w-10 bg-cream/30" />
                            <span>{textos.ceja}</span>
                        </Ceja>
                        <Titulo className="text-4xl font-medium leading-[1.02] tracking-tight md:text-7xl">
                            <ConAcento texto={textos.titular} />
                        </Titulo>
                        <Bajada className="max-w-xl text-lg leading-relaxed text-cream/60">
                            {textos.bajada}
                        </Bajada>
                    </div>

                    <ul
                        ref={listRef}
                        onMouseMove={onMove}
                        onMouseLeave={() => setActivo(null)}
                        className="group/lista mt-16 border-b border-cream/15 md:mt-24 lg:max-w-[58%]"
                    >
                        {servicios.map((servicio, i) => (
                            <li key={servicio.url} data-reveal className="border-t border-cream/15">
                                <NavLink
                                    to={`/services/${servicio.url}`}
                                    onMouseEnter={() => { setActivo(servicio); navBarEnter(); }}
                                    onFocus={() => setActivo(servicio)}
                                    onBlur={() => setActivo(null)}
                                    className="group/fila grid grid-cols-[2.5rem_1fr_auto] items-center gap-4 py-6 transition-opacity duration-500 focus-visible:outline-none md:grid-cols-[4rem_1fr_auto] md:py-8 lg:group-hover/lista:opacity-30 lg:hover:!opacity-100 focus-visible:!opacity-100"
                                >
                                    <span className="self-start pt-2 text-sm tabular-nums text-cream/40 md:pt-4">
                                        {String(i + 1).padStart(2, '0')}
                                    </span>
                                    <span className="flex flex-col gap-2">
                                        <span className="font-serif text-4xl italic leading-none transition-transform duration-500 ease-expo group-hover/fila:translate-x-3 md:text-6xl">
                                            {servicio.nombre}
                                        </span>
                                        <span className="text-sm text-cream/50 md:text-base">{bajada(servicio)}</span>
                                    </span>
                                    <span className="flex h-12 w-12 items-center justify-center rounded-full ring-1 ring-inset ring-cream/25 transition-all duration-500 ease-expo group-hover/fila:rotate-[-45deg] group-hover/fila:bg-cream group-hover/fila:text-ink group-focus-visible/fila:bg-cream group-focus-visible/fila:text-ink">
                                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5" aria-hidden="true">
                                            <path d="M5 12h14M13 6l6 6-6 6" />
                                        </svg>
                                    </span>
                                </NavLink>
                            </li>
                        ))}
                    </ul>
                </div>

                {/* Vista previa flotante (solo desktop): sigue al cursor sobre la lista */}
                <div
                    ref={previewRef}
                    aria-hidden="true"
                    className="pointer-events-none absolute left-0 top-0 z-20 hidden lg:block"
                >
                    <div
                        className={`relative h-56 w-72 -translate-x-1/2 -translate-y-1/2 overflow-hidden rounded-2xl bg-cream/5 ring-1 ring-cream/10 backdrop-blur-sm transition-all duration-500 ease-expo ${activo ? 'scale-100 opacity-100' : 'scale-75 opacity-0'}`}
                    >
                        {servicios.map((s) => (
                            <img
                                key={s.url}
                                src={PREVIEWS[s.url] || fotoServicio(s, 640)}
                                alt=""
                                loading="lazy"
                                className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-300 ${activo?.url === s.url ? 'opacity-100' : 'opacity-0'}`}
                            />
                        ))}
                    </div>
                </div>
            </section>
        </BgColorContainer>
    );
}

export default Servicios;
